/* eslint-disable */

import type { Address } from "viem"
import type { Config } from "wagmi"

import type { ApprovalMode, ContractRiskLevel, EmitEvent } from "@/domains/core"
import type { CreateBribeResult } from "@/domains/on-chain"
import { determineApprovalAmount, EventEmitter, makeApprovalDescription } from "@/domains/library"
import { approveOwnerSpenderAllowance, BIGINT_ZERO, submitBribe } from "@/domains/on-chain"

interface CreateBribeParams {
  amount: bigint
  account: Address
  chainId: number
  config: Config
  selectedToken: any
  bribeAddress: Address
  emitter: EventEmitter<EmitEvent>
  lens: CreateBribeResult
  approvalMode: ApprovalMode
  bribeType: "vote" | "lp"
}

export const createBribeInteraction = async ({
  amount,
  account,
  chainId,
  config,
  selectedToken,
  bribeAddress,
  emitter,
  lens,
  approvalMode,
  bribeType,
}: CreateBribeParams) => {
  if (amount <= BIGINT_ZERO) {
    emitter.emit("invalid-amount", { title: "You must enter a value greater than 0", variant: "info" })
    return false
  }

  // Check balance
  if (amount > lens.accountBalanceToken.wei) {
    emitter.emit("invalid-balance-in-wallet", {
      title: `You do not have enough ${selectedToken.symbol} to perform bribe`,
      variant: "info",
    })
    return false
  }

  // Check if we need approval
  const currentAllowance = lens.gaugeSpendTokenAllowance.wei
  const maxApprovalAmount = BigInt("0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff")

  // Skip approval if we already have max approval or sufficient allowance
  if (currentAllowance >= maxApprovalAmount || currentAllowance >= amount) {
    // Already approved, proceed with bribe
  } else {
    // Only do approval if needed
    const contractRiskLevel: ContractRiskLevel = "immutable"
    const actualAmountToApprove = determineApprovalAmount(amount, approvalMode, contractRiskLevel)

    emitter.emit("approve-token-allowance", {
      title: "Approving spending tokens",
      description: makeApprovalDescription(actualAmountToApprove, selectedToken?.decimals),
      variant: "info",
    })

    const approval = await approveOwnerSpenderAllowance({
      account,
      chainId,
      config,
      contractAddress: selectedToken.address,
      spenderAddress: bribeAddress,
      spenderAmount: amount,
      approvalMode,
      contractRiskLevel,
    })

    if (approval.success) {
      emitter.emit("approve-token-allowance", { title: "Approval successful", variant: "success" })
    }
    if (!approval.success) {
      emitter.emit("approve-token-allowance", {
        title: "Unable to approve spending tokens",
        description: approval.error,
        variant: "error",
      })
      return false
    }
  }

  // Submit bribe
  const submitted = await submitBribe({
    account,
    chainId,
    config,
    bribeAddress,
    tokenAddress: selectedToken.address,
    amount,
    bribeType,
  })

  if (submitted.success) {
    emitter.emit("bribe", { title: "Bribe successful", variant: "success" })
    return submitted
  }

  if (!submitted.success) {
    if (submitted.error.includes("too many rewards tokens")) {
      emitter.emit("bribe", {
        title: "Unable to add bribe",
        description:
          "This gauge has maxed out its available reward token slots, please contact the team for assistance.",
        variant: "error",
      })
      return false
    }

    emitter.emit("bribe", {
      title: "Unable to add bribe",
      description:
        submitted.error === "EGPE"
          ? "Your LP bribe would reduce the ongoing APR. Please increase your amounts!"
          : submitted.error,
      variant: "error",
    })
    return false
  }

  return submitted
}
